import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";

const initial = { name: "", email: "", phone: "", course: "", message: "" };

export default function InquiryForm({ variant = "card" }) {
  const { settings } = useSettings();
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const courses = settings?.courses?.items || [];
  const otherPrograms = settings?.courses?.otherPrograms || [];

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.course) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/inquiries", form);
      toast.success("Thank you! Our team will contact you shortly.");
      setForm(initial);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const wrapCls =
    variant === "card"
      ? "bg-white rounded-2xl border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-6 sm:p-8"
      : "";

  return (
    <form onSubmit={submit} className={wrapCls} data-testid="inquiry-form">
      <div className="mb-6">
        <p className="text-xs font-bold uppercase tracking-[0.2em] brand-secondary-text mb-2">Apply Now</p>
        <h3 className="font-heading text-2xl sm:text-3xl text-slate-900">Start your admission journey</h3>
        <p className="text-slate-500 text-sm mt-2">
          Fill in the form and our admissions team will reach out to guide you.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="in-name" className="text-slate-700 text-sm">Full Name *</Label>
          <Input id="in-name" data-testid="inquiry-input-name" value={form.name} onChange={onChange("name")} placeholder="Your full name"
            className="mt-1.5 h-11 rounded-xl border-slate-200 focus:ring-2" required />
        </div>
        <div>
          <Label htmlFor="in-email" className="text-slate-700 text-sm">Email *</Label>
          <Input id="in-email" data-testid="inquiry-input-email" type="email" value={form.email} onChange={onChange("email")} placeholder="you@example.com"
            className="mt-1.5 h-11 rounded-xl border-slate-200" required />
        </div>
        <div>
          <Label htmlFor="in-phone" className="text-slate-700 text-sm">Phone *</Label>
          <Input id="in-phone" data-testid="inquiry-input-phone" value={form.phone} onChange={onChange("phone")} placeholder="+91 ..."
            className="mt-1.5 h-11 rounded-xl border-slate-200" required />
        </div>
        <div>
          <Label className="text-slate-700 text-sm">Course of Interest *</Label>
          <Select value={form.course} onValueChange={(v) => setForm((f) => ({ ...f, course: v }))}>
            <SelectTrigger data-testid="inquiry-select-course" className="mt-1.5 h-11 rounded-xl border-slate-200">
              <SelectValue placeholder="Select a course" />
            </SelectTrigger>
            <SelectContent>
              {courses.map((c) => (
                <SelectItem key={c.slug || c.title} value={c.title} data-testid={`option-${(c.slug || c.title).toLowerCase()}`}>
                  {c.title}
                </SelectItem>
              ))}
              {otherPrograms.map((p) => (
                <SelectItem key={p} value={p} data-testid={`option-${p.toLowerCase()}`}>{p}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="mt-4">
        <Label htmlFor="in-msg" className="text-slate-700 text-sm">Message (optional)</Label>
        <Textarea id="in-msg" data-testid="inquiry-input-message" value={form.message} onChange={onChange("message")}
          placeholder="Tell us more about your interests..." className="mt-1.5 rounded-xl border-slate-200 min-h-[110px]" />
      </div>

      <Button
        type="submit"
        data-testid="inquiry-submit-btn"
        disabled={loading}
        style={{ backgroundColor: "var(--brand-primary)" }}
        className="mt-6 h-12 rounded-full text-white px-8 font-medium shadow-sm hover:shadow-md hover:-translate-y-0.5 hover:brightness-110 transition-all"
      >
        {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
        Submit Application
      </Button>
    </form>
  );
}
